import CRUDSettings from '../components/General/CRUDSettings';
import { ColDef } from 'ag-grid-community';
import {
    useCreateForecaster,
    useForecasters,
    useUpdateForecaster,
    useDeleteForecaster,
} from '../hooks/api/forecasters';
import { useHEMTUsers } from '../hooks/api/useHEMTUsers';
import React, { useCallback, useState } from 'react';
import ForecasterDialog from '../components/Forecasters/ForecasterDialog';
import { Forecaster, ForecasterForm } from '../types/Forecaster.interface';
import ConfirmationDialog from '../components/UI/Dialogs/ConfirmationDialog';

const Forecasters = () => {
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [selectedForecaster, setSelectedForecaster] = useState<Forecaster | undefined>(undefined);

    const { data: forecasters, isLoading: isForecastersLoading } = useForecasters();
    const { data: users } = useHEMTUsers();
    const { mutate: handleCreateForecaster } = useCreateForecaster({
        onSettled: () => {},
    });
    const { mutate: handleUpdateForecaster } = useUpdateForecaster({
        onSettled: () => {},
    });
    const { mutate: handleDeleteForecaster } = useDeleteForecaster({
        onSettled: (data, error) => {
            if (!error) {
                setSelectedForecaster(undefined);
                setShowConfirmationDialog(false);
            }
        },
    });

    const handleDialogSubmit = useCallback(
        (data: ForecasterForm) => {
            if (selectedForecaster) {
                handleUpdateForecaster({
                    id: selectedForecaster.id,
                    name: data.name,
                    userId: data.userId,
                });
            } else {
                handleCreateForecaster({
                    name: data.name,
                    userId: data.userId,
                });
            }
            setShowDialog(false);
        },
        [handleCreateForecaster, handleUpdateForecaster, selectedForecaster]
    );

    const onDeleteForecaster = useCallback(async (data: Forecaster) => {
        setSelectedForecaster(data);
        setShowConfirmationDialog(true);
    }, []);

    const confirmForecasterDeletion = useCallback(
        (data: Forecaster) => {
            handleDeleteForecaster(data.id);
            setShowConfirmationDialog(false);
        },
        [handleDeleteForecaster]
    );

    const columnDefs: ColDef[] = [
        {
            field: 'name',
            headerName: 'Name',
        },
        {
            field: 'userId',
            headerName: 'User ID',
        },
    ];

    return (
        <>
            <CRUDSettings
                tableRows={forecasters}
                columnDefs={columnDefs}
                btnText="Neuer Forecaster"
                createRecord={handleCreateForecaster}
                updateRecord={handleUpdateForecaster}
                recordType="Forecaster"
                isCreationViaDialog={true}
                isEditViaDialog={true}
                setShowCreationDialog={setShowDialog}
                loading={isForecastersLoading}
                onSelectedRowChange={setSelectedForecaster}
                showDeleteButton={true}
                onDelete={onDeleteForecaster}
            />
            {showDialog && (
                <ForecasterDialog
                    forecaster={selectedForecaster}
                    showDialog={showDialog}
                    users={users}
                    onSubmit={handleDialogSubmit}
                    isLoading={false}
                    onClose={() => setShowDialog(false)}
                />
            )}
            {showConfirmationDialog && selectedForecaster && (
                <ConfirmationDialog
                    open={showConfirmationDialog}
                    title="Forecaster löschen"
                    text={`Möchten Sie diesen Forecaster ${selectedForecaster.name} wirklich löschen?`}
                    onClose={() => setShowConfirmationDialog(false)}
                    onConfirm={() => confirmForecasterDeletion(selectedForecaster)}
                />
            )}
        </>
    );
};

export default Forecasters;
